import { Stack, Text } from "@mantine/core"
import { Button, EqualWidthGrid } from "@tfg/ui"
import type { AxiosError } from "axios"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { standardEmail } from "@tfg/types/schemas/utils"

import { LoginTextInput } from "~/pages/Auth/components/LoginTextInput"
import type { LoginState } from "~/pages/Auth/hooks/useLoginFlow"

import { PasswordForm } from "./PasswordForm"
import { SuccessMessage } from "./SuccessMessage"
import { TwoFactorForm } from "./TwoFactorForm"
import { UsernameForm } from "./UsernameForm"

const _usernameSchema = z.object({
  username: standardEmail(),
})

type LoginContentProps = {
  pageState: LoginState
  email: string
  userId: string
  isLoggingIn: boolean
  isSendingUsername: boolean
  isSendingResetPasswordEmail: boolean
  loginError: AxiosError | null
  usernameError: AxiosError | null
  forwardedError: Error | null
  forwardedErrorTitle: string | undefined
  onUsernameSubmit: (values: z.infer<typeof _usernameSchema>) => Promise<void>
  onClearMagicLinkError: () => void
  onLogin: (values: {
    username: string
    password: string
    recaptchaToken?: string
  }) => Promise<void>
  onVerifyTwoFactor: (values: {
    token: string
    userId: string
  }) => Promise<void>
  onForgotPassword: () => Promise<void>
  onGoToForgotPassword: () => void
  onBackToUsername: () => void
}

export const LoginContent = ({
  pageState,
  email,
  userId,
  isLoggingIn,
  isSendingUsername,
  isSendingResetPasswordEmail,
  loginError,
  usernameError,
  forwardedError,
  forwardedErrorTitle,
  onUsernameSubmit,
  onClearMagicLinkError,
  onLogin,
  onVerifyTwoFactor,
  onForgotPassword,
  onGoToForgotPassword,
  onBackToUsername,
}: LoginContentProps) => {
  const { t } = useTranslation("account")

  switch (pageState) {
    case "username":
      return (
        <UsernameForm
          onSubmit={async (values) => {
            onClearMagicLinkError()
            await onUsernameSubmit(values)
          }}
          isLoading={isSendingUsername}
          error={forwardedError ?? usernameError}
          errorTitle={forwardedErrorTitle}
        />
      )
    case "password":
      return (
        <PasswordForm
          onSubmit={onLogin}
          onForgotPassword={onGoToForgotPassword}
          isLoading={isLoggingIn}
          error={loginError}
          username={email}
        />
      )
    case "twoFactor":
      return <TwoFactorForm onSubmit={onVerifyTwoFactor} userId={userId} />
    case "forgotPassword":
      return (
        <>
          <Stack w="100%">
            <Text style={{ textAlign: "center" }}>
              {t("login.forgotPassword.title")}
            </Text>
            <LoginTextInput
              name="email"
              type="email"
              required
              autoFocus
              value={email}
              label={t("login.labels.email")}
              placeholder={t("login.placeholders.email")}
            />
          </Stack>
          <EqualWidthGrid>
            <Button
              size="sm"
              fullWidth={true}
              disabled={!email}
              loading={isSendingResetPasswordEmail}
              onClick={onForgotPassword}
            >
              {t("login.forgotPassword.submit")}
            </Button>
            <Button
              size="sm"
              fullWidth={true}
              variant="secondary"
              disabled={isSendingResetPasswordEmail}
              onClick={onBackToUsername}
            >
              {t("login.forgotPassword.back")}
            </Button>
          </EqualWidthGrid>
        </>
      )
    case "resetPasswordEmailSent":
      return (
        <SuccessMessage
          message={t("login.forgotPassword.success")}
          onBack={onBackToUsername}
          isLoading={isSendingResetPasswordEmail}
        />
      )
    case "magicLinkEmailSent":
      return (
        <SuccessMessage
          message={t("login.magicLink.success")}
          isLoading={isSendingResetPasswordEmail}
        />
      )
  }
}
